import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import UrgencyBanner, { URGENCY_UPDATED } from "@/components/UrgencyBanner";
import LookPicker from "@/components/admin/LookPicker";
import SeasonalImportDialog from "@/components/admin/SeasonalImportDialog";
import { ExternalLink, Download, Sparkles } from "lucide-react";

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const label = "font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1";
const input = "w-full brut bg-white px-3 py-2 focus:outline-none text-sm";

const hydrate = (data) => ({
  activeId: data.active_id,
  items: data.items.map((c) => ({ ...c, end_at: toLocal(c.end_at), tagsText: (c.pick_tags || []).join(", "),
    queriesText: (c.import_queries || []).map((q) => `${q.query} | ${q.tags.join(", ")}`).join("\n") })),
});

const SeasonalPicks = ({ c, categories, onChange }) => {
  const toggle = (slug) =>
    onChange({ pick_categories: c.pick_categories.includes(slug) ? c.pick_categories.filter((x) => x !== slug) : [...c.pick_categories, slug] });
  return (
    <div className="border-t-2 border-dashed border-black/30 pt-3 space-y-3" data-testid={`campaign-picks-${c.id}`}>
      <div className="font-mono uppercase text-[10px] tracking-widest">Seasonal "Complete the look" picks · 2 slots while live</div>
      <div>
        <label className={label}>Pick from categories</label>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button type="button" key={cat.slug} onClick={() => toggle(cat.slug)} data-testid={`campaign-pick-cat-${c.id}-${cat.slug}`}
              className={`brut px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press ${c.pick_categories.includes(cat.slug) ? "pcs-bg-pink text-white" : "bg-white"}`}>
              {cat.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={label}>Pick by tags (comma-separated)</label>
        <input value={c.tagsText} onChange={(e) => onChange({ tagsText: e.target.value })} placeholder="christmas, santa, elf" data-testid={`campaign-pick-tags-${c.id}`} className={input} />
      </div>
      <LookPicker value={c.pick_slugs} onChange={(pick_slugs) => onChange({ pick_slugs })} label="Pinned seasonal products" hint="shown first" testId={`campaign-pick-slugs-${c.id}`} />
      <div>
        <label className={label}>Seasonal import searches (one per line: search | tags)</label>
        <textarea rows={3} value={c.queriesText} onChange={(e) => onChange({ queriesText: e.target.value })} placeholder="santa suit costume | christmas, santa" data-testid={`campaign-import-queries-${c.id}`} className={`${input} font-mono text-xs`} />
      </div>
    </div>
  );
};

const CampaignRow = ({ c, active, categories, onChange, onRemove, onImport, onGuide, guiding }) => (
  <div className={`brut p-4 space-y-3 ${active ? "bg-[#FFF4E5]" : "bg-white"}`} data-testid={`campaign-${c.id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <input value={c.name} onChange={(e) => onChange({ name: e.target.value })} data-testid={`campaign-name-${c.id}`} className="font-display text-xl tracking-tight bg-transparent border-b-2 border-black/20 focus:border-black focus:outline-none" />
      {active && <span className="brut pcs-bg-pink text-white px-2 py-0.5 font-mono uppercase text-[10px] tracking-widest" data-testid={`campaign-live-${c.id}`}>Live now</span>}
      {c.preset && <span className="font-mono uppercase text-[10px] tracking-widest opacity-60">preset · {c.preset}</span>}
      {c.available_picks === 0 && (
        <span className="brut bg-[#FFE600] px-2 py-0.5 font-mono uppercase text-[10px] tracking-widest" data-testid={`campaign-no-picks-${c.id}`}>
          0 available picks — import or pin some
        </span>
      )}
      {c.available_picks > 0 && <span className="font-mono uppercase text-[10px] tracking-widest opacity-60" data-testid={`campaign-picks-count-${c.id}`}>{c.available_picks} picks</span>}
      <label className="ml-auto inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={c.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} data-testid={`campaign-enabled-${c.id}`} className="w-4 h-4" />
        <span className="font-mono uppercase text-[10px] tracking-widest">On</span>
      </label>
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={c.repeat_yearly} onChange={(e) => onChange({ repeat_yearly: e.target.checked })} data-testid={`campaign-repeat-${c.id}`} className="w-4 h-4" />
        <span className="font-mono uppercase text-[10px] tracking-widest">Repeat yearly</span>
      </label>
      <a href={`/season/${c.slug || c.id}`} target="_blank" rel="noreferrer" data-testid={`campaign-view-${c.id}`} className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1">
        <ExternalLink className="w-3 h-3" strokeWidth={3} /> Page
      </a>
      <button type="button" onClick={onImport} data-testid={`campaign-import-${c.id}`} className="brut pcs-bg-blue text-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1">
        <Download className="w-3 h-3" strokeWidth={3} /> Import products
      </button>
      <button type="button" onClick={onGuide} disabled={guiding} data-testid={`campaign-gift-guide-${c.id}`} className="brut pcs-bg-orange text-black px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1 disabled:opacity-50">
        <Sparkles className={`w-3 h-3 ${guiding ? "animate-spin" : ""}`} strokeWidth={3} /> {guiding ? "Writing (~1 min)..." : "Draft gift guide"}
      </button>
      <button type="button" onClick={onRemove} data-testid={`campaign-remove-${c.id}`} className="brut bg-white p-1.5 brut-press" aria-label="Remove">
        <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
    <div>
      <label className={label}>Message</label>
      <input value={c.message} onChange={(e) => onChange({ message: e.target.value })} data-testid={`campaign-message-${c.id}`} className={input} />
    </div>
    <div>
      <label className={label}>Landing page URL</label>
      <div className="flex items-center brut bg-white">
        <span className="px-2 font-mono text-xs opacity-60">/season/</span>
        <input value={c.slug} onChange={(e) => onChange({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid={`campaign-slug-${c.id}`} className="flex-1 py-2 pr-2 focus:outline-none text-sm" />
      </div>
    </div>
    <div className="grid md:grid-cols-3 gap-3">
      <div>
        <label className={label}>Countdown ends (your local time)</label>
        <input type="datetime-local" value={c.end_at} onChange={(e) => onChange({ end_at: e.target.value })} data-testid={`campaign-end-${c.id}`} className={input} />
        {c.effective_end && (
          <div className="font-mono text-[10px] opacity-60 mt-1">Next run ends {new Date(c.effective_end).toLocaleString()}</div>
        )}
      </div>
      <div>
        <label className={label}>Button link</label>
        <input value={c.link_url} onChange={(e) => onChange({ link_url: e.target.value })} data-testid={`campaign-link-url-${c.id}`} className={input} />
      </div>
      <div>
        <label className={label}>Button label</label>
        <input value={c.link_label} onChange={(e) => onChange({ link_label: e.target.value })} data-testid={`campaign-link-label-${c.id}`} className={input} />
      </div>
    </div>
    <SeasonalPicks c={c} categories={categories} onChange={onChange} />
  </div>
);

export default function UrgencyPanel({ onOpenArticle }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [importing, setImporting] = useState(null);
  const [guiding, setGuiding] = useState(null);
  const draftGuide = async (c) => {
    if (!saved?.some((x) => x.id === c.id)) return toast.error("Save this campaign first");
    setGuiding(c.id);
    try {
      const { data: job } = await api.post(`/admin/campaigns/${c.id}/gift-guide`);
      let data = job;
      for (let i = 0; i < 60 && data.status === "running"; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        data = (await api.get(`/admin/ai-jobs/${job.job_id}`)).data;
      }
      if (data.status !== "done") throw { response: { data: { detail: data.error || "Gift guide timed out" } } };
      toast.success(`Draft saved: ${data.title}`, {
        action: { label: "Open", onClick: () => onOpenArticle(data.slug) },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gift guide failed");
    } finally {
      setGuiding(null);
    }
  };
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    api.get("/admin/settings/campaigns").then((r) => { setState(hydrate(r.data)); setSaved(r.data.items); });
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  if (!state) return <div className="font-mono uppercase text-xs tracking-widest opacity-60">Loading...</div>;

  const setItems = (fn) => setState((s) => ({ ...s, items: fn(s.items) }));
  const patch = (id, p) => setItems((items) => items.map((c) => (c.id === id ? { ...c, ...p } : c)));
  const add = () =>
    setItems((items) => [
      ...items,
      { id: crypto.randomUUID(), name: "New campaign", preset: null, enabled: true, repeat_yearly: false, message: "", end_at: toLocal(new Date(Date.now() + 7 * 864e5).toISOString()), link_url: "", link_label: "", pick_categories: [], pick_tags: [], pick_slugs: [], tagsText: "", slug: "", import_queries: [], queriesText: "" },
    ]);

  const save = async () => {
    if (state.items.some((c) => !c.message.trim() || !c.end_at)) return toast.error("Every campaign needs a message and end date");
    setBusy(true);
    try {
      const items = state.items.map(({ effective_end, tagsText, queriesText, ...c }) => ({
        ...c,
        import_queries: queriesText.split("\n").map((l) => l.split("|")).filter(([q]) => q.trim()).map(([q, t = ""]) => ({
          query: q.trim(),
          tags: t.split(",").map((x) => x.trim().toLowerCase()).filter(Boolean),
        })),
        end_at: new Date(c.end_at).toISOString(),
        pick_tags: tagsText.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
      }));
      const { data } = await api.put("/admin/settings/campaigns", { items });
      setState(hydrate(data));
      setSaved(data.items);
      window.dispatchEvent(new Event(URGENCY_UPDATED));
      toast.success("Campaigns saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="urgency-panel">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5" strokeWidth={2.5} />
        <h2 className="font-display text-2xl tracking-tight">Banner campaigns</h2>
        <p className="text-xs opacity-70 ml-2 hidden md:block">The next campaign that hasn't ended yet shows sitewide and hands over automatically.</p>
      </div>
      {state.items.map((c) => (
        <CampaignRow key={c.id} c={c} categories={categories} active={c.id === state.activeId} onChange={(p) => patch(c.id, p)} onRemove={() => setItems((items) => items.filter((x) => x.id !== c.id))}
          guiding={guiding === c.id}
          onGuide={() => draftGuide(c)}
          onImport={() => {
            const s = saved?.find((x) => x.id === c.id);
            if (!s) return toast.error("Save this campaign before importing");
            setImporting(s);
          }} />
      ))}
      <div className="flex justify-between">
        <button onClick={add} data-testid="campaign-add" className="brut bg-white px-3 py-2 font-bold uppercase text-xs tracking-wide brut-press inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" strokeWidth={3} /> Add campaign
        </button>
        <button onClick={save} disabled={busy} data-testid="urgency-save" className="brut pcs-bg-pink text-white px-4 py-2 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50">
          {busy ? "Saving..." : "Save campaigns"}
        </button>
      </div>
      {importing && <SeasonalImportDialog campaign={importing} onClose={() => setImporting(null)} />}
      <div>
        <div className={label}>Live preview (after save)</div>
        <div className="border-2 border-black" data-testid="urgency-preview"><UrgencyBanner preview /></div>
      </div>
    </div>
  );
}
